import API_BASE_URL from 'src/constants';
import { $api } from './api';
import { components, paths } from '../generated/v1';
import { getToken, handleUnauthorized } from './authToken';

export type Invoice = components['schemas']['Invoice'];
export type InvoiceDetail = components['schemas']['InvoiceDetail'];
export type InvoiceLine = components['schemas']['InvoiceLine'];
export type InvoiceSummary = components['schemas']['InvoiceSummary'];
export type InvoiceStatus = Invoice['status'];
export type InvoiceTab = NonNullable<NonNullable<paths['/invoices']['get']['parameters']['query']>['tab']>;

export const INVOICE_TABS: InvoiceTab[] = ['all', 'open', 'overdue', 'paid', 'credit_notes'];

export const useInvoicesQuery = (params: paths['/invoices']['get']['parameters']['query']) =>
  $api.useQuery('get', '/invoices', { params: { query: params } });

export const useInvoiceSummaryQuery = () => $api.useQuery('get', '/invoices/summary', {});

export const useInvoiceQuery = (id: number) =>
  $api.useQuery('get', '/invoices/{invoice_id}', { params: { path: { invoice_id: id } } }, { enabled: id > 0 });

export class InvoicePdfError extends Error {
  status: number;

  constructor(status: number) {
    super(`invoice pdf failed (${status})`);
    this.status = status;
  }
}

/** Fetches the PDF with the bearer token and hands it to the browser as a download. */
export async function downloadInvoicePdf(invoiceId: number, fallbackName: string): Promise<void> {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}portal/invoices/${invoiceId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) handleUnauthorized(token);
  if (!response.ok) throw new InvoicePdfError(response.status);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `${fallbackName.replace(/[^A-Za-z0-9._-]/g, '_')}.pdf`;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
